import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminSectionCard,
  ConfirmDialog,
  HelpAnchor,
  InlineError,
  InputField,
  Pill,
  ProviderLogo,
  SkeletonBlock,
  SkeletonForm,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  ADMIN_ACTION_BUTTON,
  BUTTON_DANGER,
  BUTTON_PRIMARY,
  SECTION_DIVIDER,
} from '@/shared/ui-class';
import {
  SEED_API_KEYS_LOAD,
  type ApiKeyProvider,
  type ApiKeyStatus,
  type ApiKeysLoadResult,
  type BedrockModelHealth,
  type ClaudeAnthropicAwsConfig,
  type ClaudeProviderMode,
  type ClaudeProviderModeStatus,
  type DaytonaUrlStatus,
  type ProbeResult,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  API_KEY_PROVIDERS,
  aggregateBedrockHealth,
  apiKeyToLogoProvider,
  bedrockAggregatePill,
  formatBedrockDegradedNote,
  formatCheckedAt,
  formatRelative,
  initialDaytonaUrlRowState,
  initialRowStateMap,
  maskedDbStatus,
  mockClearApiKey,
  mockProbeOk,
  mockSaveApiKey,
  modelEntryPill,
  modelShortLabel,
  probePill,
  PROVIDER_ENV_VARS,
  PROVIDER_LABELS,
  PROVIDER_SUPPORTS_PROBE,
  SEGMENT_LABELS,
  type DaytonaUrlRowState,
  type RowState,
} from './ApiKeysScene.transform';

export function BedrockHealthTrace({ health }: { health: BedrockModelHealth }) {
  const hasPropagating = health.models.some((m) => m.errorCode === "PROPAGATING");
  const aggregate = aggregateBedrockHealth(health);
  const pill =
    hasPropagating && aggregate === "unknown"
      ? { label: "Propagating…", tone: "warning" as const }
      : bedrockAggregatePill(aggregate);

  return (
    <>
      <Pill tone={pill.tone}>{pill.label}</Pill>
      {aggregate === "degraded" && !hasPropagating ? (
        <p className="w-full text-sm text-amber-700 dark:text-amber-400">
          {formatBedrockDegradedNote(health)}
        </p>
      ) : null}
      <ul className="w-full space-y-0.5">
        {health.models.map((entry) => {
          const entryPill = modelEntryPill(entry);
          return (
            <li
              key={entry.modelId}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
            >
              <span className="text-zinc-700 dark:text-zinc-300">
                {modelShortLabel(entry.modelId)}
              </span>
              <Pill tone={entryPill.tone}>{entryPill.label}</Pill>
              {entry.errorMessage && entry.errorCode !== "PROPAGATING" ? (
                <span
                  className={
                    entryPill.tone === "danger"
                      ? "text-red-600 dark:text-red-400"
                      : "text-amber-700 dark:text-amber-400"
                  }
                >
                  {entry.errorMessage}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function ApiKeysPanelSkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      <AdminSectionCard title="Anthropic">
        <SkeletonForm fields={2} />
      </AdminSectionCard>
      {API_KEY_PROVIDERS.map((provider) => (
        <AdminSectionCard key={provider}>
          <SkeletonBlock className="h-4 w-40" />
          <SkeletonForm fields={2} className="mt-4" />
        </AdminSectionCard>
      ))}
    </div>
  );
}

function ApiKeysPanelBody() {
  const [statuses, setStatuses] = useState<ApiKeyStatus[]>(SEED_API_KEYS_LOAD.statuses);
  const [probeResults, setProbeResults] = useState<ApiKeysLoadResult["probeResults"]>(SEED_API_KEYS_LOAD.probeResults ?? {});
  const [daytonaUrlStatus, setDaytonaUrlStatus] = useState<DaytonaUrlStatus | null>(SEED_API_KEYS_LOAD.daytonaUrl ?? null);
  const [daytonaUrlRow, setDaytonaUrlRow] = useState<DaytonaUrlRowState>(
    initialDaytonaUrlRowState,
  );
  const [claudeProviderMode, setClaudeProviderMode] = useState<ClaudeProviderModeStatus | null>(SEED_API_KEYS_LOAD.claudeProviderMode ?? null);
  const [bedrockModelHealth, setBedrockModelHealth] = useState<BedrockModelHealth | null>(SEED_API_KEYS_LOAD.bedrockModelHealth ?? null);
  const [providerModeBusy, setProviderModeBusy] = useState(false);
  const [providerModeError, setProviderModeError] = useState<string | null>(null);
  const [anthropicAwsConfig, setAnthropicAwsConfig] = useState<ClaudeAnthropicAwsConfig | null>(SEED_API_KEYS_LOAD.anthropicAwsConfig ?? null);
  const [awsWorkspaceIdInput, setAwsWorkspaceIdInput] = useState(SEED_API_KEYS_LOAD.anthropicAwsConfig?.workspaceId ?? "");
  const [awsRegionInput, setAwsRegionInput] = useState(SEED_API_KEYS_LOAD.anthropicAwsConfig?.region ?? "us-east-1");
  const [awsConfigBusy, setAwsConfigBusy] = useState(false);
  const [awsConfigError, setAwsConfigError] = useState<string | null>(null);
  const [anthropicAwsProbe, setAnthropicAwsProbe] = useState<ProbeResult | null>(SEED_API_KEYS_LOAD.anthropicAwsProbe ?? null);
  const [anthropicAwsTesting, setAnthropicAwsTesting] = useState(false);
  const [anthropicAwsTestError, setAnthropicAwsTestError] = useState<
    string | null
  >(null);
  const [selectedSegment, setSelectedSegment] = useState<ClaudeProviderMode>(SEED_API_KEYS_LOAD.claudeProviderMode?.mode ?? "anthropic-native");
  const [loading] = useState(false);
  const [loadError] = useState<string | null>(null);

  // Collapsible provider cards — collapsed by default; chevron at the row end.
  const [openCards, setOpenCards] = useState<Set<ApiKeyProvider>>(
    () => new Set<ApiKeyProvider>(),
  );
  const toggleCard = useCallback((provider: ApiKeyProvider) => {
    setOpenCards((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  }, []);
  const openCard = useCallback((provider: ApiKeyProvider) => {
    setOpenCards((prev) => (prev.has(provider) ? prev : new Set(prev).add(provider)));
  }, []);
  const [rowState, setRowState] = useState<Record<ApiKeyProvider, RowState>>(initialRowStateMap());

  const updateRow = (
    provider: ApiKeyProvider,
    patch: Partial<RowState>,
  ): void => {
    setRowState((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], ...patch },
    }));
  };

  const handleSave = async (provider: ApiKeyProvider): Promise<void> => {
    const row = rowState[provider];
    updateRow(provider, { busy: true, error: null });
    setStatuses((prev) => mockSaveApiKey(prev, provider, row.keyInput, row.labelInput));
    updateRow(provider, {
      keyInput: '',
      labelInput: '',
      expanded: false,
      busy: false,
    });
    if (provider === 'bedrock') {
      setProbeResults((prev) => {
        const next = { ...prev };
        delete next.bedrock;
        return next;
      });
      setBedrockModelHealth(null);
      setProbeResults((prev) => ({ ...prev, bedrock: mockProbeOk('bedrock') }));
    }
  };

  const handleTest = async (provider: ApiKeyProvider): Promise<void> => {
    if (provider === 'bedrock') {
      updateRow('bedrock', { testing: true, error: null });
      setProbeResults((prev) => ({ ...prev, bedrock: mockProbeOk('bedrock') }));
      updateRow('bedrock', { testing: false });
      return;
    }
    updateRow(provider, { testing: true, error: null });
    setProbeResults((prev) => ({ ...prev, [provider]: mockProbeOk(provider) }));
    updateRow(provider, { testing: false });
  };

  const handleClear = async (provider: ApiKeyProvider): Promise<void> => {
    updateRow(provider, { busy: true, error: null });
    setStatuses((prev) => mockClearApiKey(prev, provider));
    updateRow(provider, { confirmClear: false, busy: false });
  };

  const updateDaytonaUrlRow = (patch: Partial<DaytonaUrlRowState>): void => {
    setDaytonaUrlRow((prev) => ({ ...prev, ...patch }));
  };

  const handleSaveDaytonaUrl = async (): Promise<void> => {
    updateDaytonaUrlRow({ busy: true, error: null });
    setDaytonaUrlStatus({
      url: daytonaUrlRow.urlInput,
      source: 'db',
    });
    updateDaytonaUrlRow({ expanded: false, urlInput: '', busy: false });
  };

  const handleClearDaytonaUrl = async (): Promise<void> => {
    updateDaytonaUrlRow({ busy: true, error: null });
    setDaytonaUrlStatus({
      url: 'https://app.daytona.io/api',
      source: 'default',
    });
    updateDaytonaUrlRow({ confirmClear: false, busy: false });
  };

  const handleSaveAwsConfig = async (): Promise<void> => {
    setAwsConfigBusy(true);
    setAwsConfigError(null);
    setAnthropicAwsConfig({
      workspaceId: awsWorkspaceIdInput.trim() || (anthropicAwsConfig?.workspaceId ?? null),
      region: awsRegionInput.trim() || (anthropicAwsConfig?.region ?? 'us-east-1'),
    });
    setAwsConfigBusy(false);
  };

  const handleTestAnthropicAws = async (): Promise<void> => {
    setAnthropicAwsTesting(true);
    setAnthropicAwsTestError(null);
    setAnthropicAwsProbe(mockProbeOk('claude'));
    setAnthropicAwsTesting(false);
  };

  const handleSetProviderMode = async (mode: ClaudeProviderMode): Promise<void> => {
    setProviderModeBusy(true);
    setProviderModeError(null);
    setClaudeProviderMode({ mode, source: 'db' });
    setProviderModeBusy(false);
  };

  const renderProviderRow = (provider: ApiKeyProvider): ReactNode => {
    const status =
      statuses.find((s) => s.provider === provider) ??
      ({
        provider,
        source: "none",
        last4: null,
        label: null,
        rotatedAt: null,
        setByEmail: null,
      } satisfies ApiKeyStatus);
    const row = rowState[provider];
    const envVars = PROVIDER_ENV_VARS[provider].join(", ");
    const probe = probeResults[provider];
    const badge = probe ? probePill(probe) : null;
    const showBedrockTrace =
      provider === "bedrock" && !!bedrockModelHealth?.models?.length;
    const open = openCards.has(provider);

    return (
      <div className="flex items-start gap-2.5">
        <ProviderLogo
          provider={apiKeyToLogoProvider(provider)}
          size={18}
          className="mt-0.5 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "flex items-start justify-between gap-3",
              open && "pb-4",
            )}
          >
            <button
              type="button"
              onClick={() => toggleCard(provider)}
              aria-expanded={open}
              className="flex min-w-0 flex-1 items-start gap-2 text-left"
            >
              <div className="min-w-0">
                <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {PROVIDER_LABELS[provider]}
                </h2>
                <p className="text-xs text-muted">Env fallback: {envVars}</p>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1.5">
              {PROVIDER_SUPPORTS_PROBE[provider] ? (
                <button
                  type="button"
                  className={ADMIN_ACTION_BUTTON}
                  onClick={() => {
                    openCard(provider);
                    void handleTest(provider);
                  }}
                  disabled={row.busy || row.testing}
                >
                  {row.testing ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Testing…
                    </span>
                  ) : (
                    "Test connection"
                  )}
                </button>
              ) : null}
              <button
                type="button"
                className={ADMIN_ACTION_BUTTON}
                onClick={() => {
                  openCard(provider);
                  updateRow(provider, {
                    expanded: !row.expanded,
                    error: null,
                  });
                }}
                disabled={row.busy}
              >
                {status.source === "db" ? "Rotate" : "Set"}
              </button>
              <button
                type="button"
                className={ADMIN_ACTION_BUTTON}
                onClick={() => {
                  openCard(provider);
                  updateRow(provider, { confirmClear: true, error: null });
                }}
                disabled={row.busy || status.source !== "db"}
                title={
                  status.source !== "db"
                    ? "No admin-set key to clear"
                    : undefined
                }
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => toggleCard(provider)}
                aria-expanded={open}
                aria-label={open ? "Collapse" : "Expand"}
                className="inline-flex h-9 w-8 items-center justify-center rounded-md text-muted hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                {open ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {open ? (
            <div className="min-h-[10rem]">
              <div className="mt-4 text-sm">
                {status.source === "db" ? (
                  <div className="space-y-0.5">
                    <span className="font-mono text-zinc-700 dark:text-zinc-300">
                      {maskedDbStatus(status)}
                    </span>
                    {status.rotatedAt || status.label ? (
                      <p className="text-xs text-muted">
                        {status.rotatedAt
                          ? `Rotated ${formatRelative(status.rotatedAt)}${status.setByEmail ? ` by ${status.setByEmail}` : ""}`
                          : ""}
                        {status.rotatedAt && status.label ? " · " : ""}
                        {status.label ?? ""}
                      </p>
                    ) : null}
                  </div>
                ) : status.source === "env" ? (
                  <Pill>from env</Pill>
                ) : (
                  <span className="text-sm text-muted">Not set</span>
                )}
              </div>

              {row.error ? (
                <div className="mt-3">
                  <InlineError>{row.error}</InlineError>
                </div>
              ) : null}

              {probe ? (
                <div className="mt-3 space-y-1 text-sm">
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex flex-wrap items-center gap-2"
                  >
                    {showBedrockTrace && bedrockModelHealth ? (
                      <>
                        <BedrockHealthTrace health={bedrockModelHealth} />
                        {row.testing && probe.errorCode === "PROPAGATING" ? (
                          <Loader2
                            className="h-3 w-3 shrink-0 animate-spin text-amber-700 dark:text-amber-400"
                            aria-hidden
                          />
                        ) : null}
                      </>
                    ) : badge ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Pill tone={badge.tone}>{badge.label}</Pill>
                        {row.testing &&
                        provider === "bedrock" &&
                        probe.errorCode === "PROPAGATING" ? (
                          <Loader2
                            className="h-3 w-3 animate-spin text-amber-700 dark:text-amber-400"
                            aria-hidden
                          />
                        ) : null}
                      </span>
                    ) : null}
                    <span className="text-sm text-muted">
                      Last checked {formatCheckedAt(probe.checkedAt)}
                    </span>
                  </div>
                  {probe.errorMessage && !showBedrockTrace ? (
                    <p
                      className={
                        probe.errorCode === "PROPAGATING"
                          ? "text-sm text-amber-700 dark:text-amber-400"
                          : probe.status === "error"
                            ? "text-sm text-red-600 dark:text-red-400"
                            : "text-sm text-muted"
                      }
                    >
                      {probe.errorMessage}
                    </p>
                  ) : null}
                  <Link
                    to="/admin/service-health"
                    className="text-sm text-muted underline hover:text-zinc-700 dark:hover:text-zinc-300"
                  >
                    View in API health
                  </Link>
                </div>
              ) : null}

              {provider === "daytona" && daytonaUrlStatus ? (
                <div className={cn("mt-3 pt-3", SECTION_DIVIDER)}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                        Endpoint URL
                      </p>
                      <p className="mt-0.5 break-all font-mono text-sm text-zinc-600 dark:text-zinc-400">
                        {daytonaUrlStatus.url}
                      </p>
                      {daytonaUrlStatus.source !== "db" ? (
                        <p className="mt-0.5 text-sm text-muted">
                          {daytonaUrlStatus.source === "env"
                            ? "From env (DAYTONA_API_URL)"
                            : "Built-in default"}
                        </p>
                      ) : (
                        <p className="mt-0.5 text-sm text-muted">
                          From admin config
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {daytonaUrlStatus.source === "db" ? (
                        <button
                          type="button"
                          className={BUTTON_DANGER}
                          onClick={() =>
                            updateDaytonaUrlRow({
                              confirmClear: true,
                              error: null,
                            })
                          }
                          disabled={daytonaUrlRow.busy}
                        >
                          Clear
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={ADMIN_ACTION_BUTTON}
                        onClick={() =>
                          updateDaytonaUrlRow({
                            expanded: !daytonaUrlRow.expanded,
                            error: null,
                          })
                        }
                        disabled={daytonaUrlRow.busy}
                      >
                        {daytonaUrlStatus.source === "db" ? "Update" : "Override"}
                      </button>
                    </div>
                  </div>

                  {daytonaUrlRow.error ? (
                    <div className="mt-2">
                      <InlineError>{daytonaUrlRow.error}</InlineError>
                    </div>
                  ) : null}

                  {daytonaUrlRow.expanded ? (
                    <div className="mt-3 space-y-3">
                      <label className="block text-sm text-zinc-700 dark:text-zinc-300">
                        Endpoint URL
                        <InputField
                          className="mt-1"
                          type="url"
                          autoComplete="off"
                          placeholder="https://app.daytona.io/api"
                          value={daytonaUrlRow.urlInput}
                          onChange={(e) =>
                            updateDaytonaUrlRow({ urlInput: e.target.value })
                          }
                          disabled={daytonaUrlRow.busy}
                        />
                      </label>
                      <p className="text-sm text-muted">
                        Must be an https:// URL. Changes take effect on the next
                        sandbox operation — the cached client is rebuilt
                        automatically.
                      </p>
                      <button
                        type="button"
                        className={BUTTON_PRIMARY}
                        disabled={
                          daytonaUrlRow.busy || !daytonaUrlRow.urlInput.trim()
                        }
                        onClick={() => void handleSaveDaytonaUrl()}
                      >
                        {daytonaUrlRow.busy ? "Saving…" : "Save URL"}
                      </button>
                    </div>
                  ) : null}

                  <ConfirmDialog
                    open={daytonaUrlRow.confirmClear}
                    onOpenChange={(open) =>
                      updateDaytonaUrlRow({ confirmClear: open })
                    }
                    error={
                      daytonaUrlRow.confirmClear ? daytonaUrlRow.error : undefined
                    }
                    title="Clear Daytona endpoint URL override?"
                    description={
                      <>
                        The endpoint URL will fall back to the{" "}
                        <code className="font-mono text-xs">DAYTONA_API_URL</code>{" "}
                        environment variable, or the built-in default (
                        <code className="font-mono text-xs">
                          https://app.daytona.io/api
                        </code>
                        ) if unset.
                      </>
                    }
                    confirmLabel="Clear URL"
                    tone="danger"
                    onConfirm={() => handleClearDaytonaUrl()}
                  />
                </div>
              ) : null}

              {row.expanded ? (
                <div className={cn("mt-3 space-y-3 pt-3", SECTION_DIVIDER)}>
                  <label className="block text-sm text-zinc-700 dark:text-zinc-300">
                    API key
                    <InputField
                      className="mt-1"
                      type="password"
                      autoComplete="off"
                      value={row.keyInput}
                      onChange={(e) =>
                        updateRow(provider, { keyInput: e.target.value })
                      }
                      disabled={row.busy}
                    />
                  </label>
                  <label className="block text-sm text-zinc-700 dark:text-zinc-300">
                    Label (optional)
                    <InputField
                      className="mt-1"
                      value={row.labelInput}
                      onChange={(e) =>
                        updateRow(provider, { labelInput: e.target.value })
                      }
                      disabled={row.busy}
                    />
                  </label>
                  <button
                    type="button"
                    className={BUTTON_PRIMARY}
                    disabled={row.busy || !row.keyInput.trim()}
                    onClick={() => void handleSave(provider)}
                  >
                    {row.busy ? "Saving…" : "Save"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <ConfirmDialog
            open={row.confirmClear}
            onOpenChange={(open) =>
              updateRow(provider, { confirmClear: open })
            }
            error={row.confirmClear ? row.error : undefined}
            title={`Clear ${PROVIDER_LABELS[provider]} key?`}
            description={
              <>
                If {envVars} is set in the environment, that key will take
                over. Otherwise this provider becomes unavailable until a new
                key is set.
                <span className="mt-3 block text-sm text-muted">
                  Type <span className="font-mono">{provider}</span> to
                  confirm.
                </span>
              </>
            }
            confirmLabel="Clear key"
            tone="danger"
            confirmation={{
              expected: provider,
              label: "Confirm provider",
              placeholder: provider,
            }}
            onConfirm={() => handleClear(provider)}
          />
        </div>
      </div>
    );
  };

  const selectedSegmentConfigMissing =
    selectedSegment === "anthropic-native"
      ? (statuses.find((s) => s.provider === "claude")?.source ?? "none") ===
        "none"
      : selectedSegment === "aws-bedrock"
        ? (statuses.find((s) => s.provider === "bedrock")?.source ?? "none") ===
          "none"
        : !anthropicAwsConfig?.workspaceId;

  const anthropicAwsProbeBadge = anthropicAwsProbe
    ? probePill(anthropicAwsProbe)
    : null;

  if (loading) {
    return <ApiKeysPanelSkeleton />;
  }

  if (loadError) {
    return <InlineError>{loadError}</InlineError>;
  }

  return (
    <div className="space-y-4">
      {claudeProviderMode ? (
        <AdminSectionCard
          title="Anthropic"
          actions={
            <div className="flex flex-wrap items-center gap-1.5">
              <Pill tone="success">
                Active: {SEGMENT_LABELS[claudeProviderMode.mode]}
              </Pill>
              <Pill>
                {claudeProviderMode.source === "db"
                  ? "admin override"
                  : "default"}
              </Pill>
            </div>
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={
                selectedSegment === "anthropic-native"
                  ? BUTTON_PRIMARY
                  : ADMIN_ACTION_BUTTON
              }
              onClick={() => setSelectedSegment("anthropic-native")}
            >
              Anthropic API
            </button>
            <button
              type="button"
              className={
                selectedSegment === "aws-bedrock"
                  ? BUTTON_PRIMARY
                  : ADMIN_ACTION_BUTTON
              }
              onClick={() => setSelectedSegment("aws-bedrock")}
            >
              AWS Bedrock
            </button>
            <button
              type="button"
              className={
                selectedSegment === "claude-platform-on-aws"
                  ? BUTTON_PRIMARY
                  : ADMIN_ACTION_BUTTON
              }
              onClick={() => setSelectedSegment("claude-platform-on-aws")}
            >
              Claude Platform on AWS
            </button>
            {providerModeBusy ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted" />
            ) : null}
          </div>

          <div className={cn("mt-3 pt-3", SECTION_DIVIDER)}>
            {claudeProviderMode &&
            selectedSegment !== claudeProviderMode.mode ? (
              <p className="mb-3 text-sm text-muted">
                Not the active backend — chat currently uses{" "}
                {SEGMENT_LABELS[claudeProviderMode.mode]}. Testing here checks
                the {SEGMENT_LABELS[selectedSegment]} credential only; it does
                not reflect the live chat path.
              </p>
            ) : null}
            {selectedSegment === "anthropic-native"
              ? renderProviderRow("claude")
              : null}
            {selectedSegment === "aws-bedrock" ? (
              <>
                {renderProviderRow("bedrock")}
                <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
                  Native Anthropic web_search / web_fetch server tools are not
                  available on AWS Bedrock (partner-operated). They are available
                  on the Anthropic API and Claude Platform on AWS.
                </p>
              </>
            ) : null}
            {selectedSegment === "claude-platform-on-aws" ? (
              <div className="space-y-3">
                <label className="block text-sm text-zinc-700 dark:text-zinc-300">
                  Workspace ID
                  <InputField
                    className="mt-1"
                    placeholder="wrkspc_…"
                    autoComplete="off"
                    value={awsWorkspaceIdInput}
                    onChange={(e) => setAwsWorkspaceIdInput(e.target.value)}
                    disabled={awsConfigBusy}
                  />
                </label>
                {anthropicAwsConfig?.workspaceId ? (
                  <p className="text-xs text-muted">
                    Current:{" "}
                    <span className="font-mono">
                      {anthropicAwsConfig.workspaceId}
                    </span>
                  </p>
                ) : null}
                <label className="block text-sm text-zinc-700 dark:text-zinc-300">
                  AWS Region
                  <InputField
                    className="mt-1"
                    placeholder="us-east-1"
                    autoComplete="off"
                    value={awsRegionInput}
                    onChange={(e) => setAwsRegionInput(e.target.value)}
                    disabled={awsConfigBusy}
                  />
                </label>
                {awsConfigError ? (
                  <div className="mt-1">
                    <InlineError>{awsConfigError}</InlineError>
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={BUTTON_PRIMARY}
                    disabled={
                      awsConfigBusy ||
                      (!awsWorkspaceIdInput.trim() && !awsRegionInput.trim())
                    }
                    onClick={() => void handleSaveAwsConfig()}
                  >
                    {awsConfigBusy ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    className={ADMIN_ACTION_BUTTON}
                    onClick={() => void handleTestAnthropicAws()}
                    disabled={anthropicAwsTesting || awsConfigBusy}
                  >
                    {anthropicAwsTesting ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Testing…
                      </span>
                    ) : (
                      "Test connection"
                    )}
                  </button>
                </div>
                {anthropicAwsTestError ? (
                  <div className="mt-1">
                    <InlineError>{anthropicAwsTestError}</InlineError>
                  </div>
                ) : null}
                {anthropicAwsProbe ? (
                  <div className="space-y-1 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      {anthropicAwsProbeBadge ? (
                        <Pill tone={anthropicAwsProbeBadge.tone}>
                          {anthropicAwsProbeBadge.label}
                        </Pill>
                      ) : null}
                      <span className="text-sm text-muted">
                        Last checked{" "}
                        {formatCheckedAt(anthropicAwsProbe.checkedAt)}
                      </span>
                    </div>
                    {anthropicAwsProbe.status === "error" &&
                    anthropicAwsProbe.errorMessage ? (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {anthropicAwsProbe.errorMessage}
                      </p>
                    ) : null}
                    <Link
                      to="/admin/service-health"
                      className="text-sm text-muted underline hover:text-zinc-700 dark:hover:text-zinc-300"
                    >
                      View in API health
                    </Link>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={cn("mt-3 pt-3", SECTION_DIVIDER)}>
            <div className="space-y-2">
              {claudeProviderMode.mode !== selectedSegment &&
              selectedSegmentConfigMissing ? (
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {selectedSegment === "anthropic-native"
                    ? "Set an Anthropic API key below before activating, or turns will fail."
                    : selectedSegment === "aws-bedrock"
                      ? "Set the AWS Bedrock bearer token + region below before activating."
                      : "Set the workspace ID + region below before activating."}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                {claudeProviderMode.mode === selectedSegment ? (
                  <Pill tone="success">Active</Pill>
                ) : null}
                <button
                  type="button"
                  className={BUTTON_PRIMARY}
                  disabled={
                    providerModeBusy ||
                    claudeProviderMode.mode === selectedSegment
                  }
                  onClick={() => void handleSetProviderMode(selectedSegment)}
                >
                  Make active
                </button>
              </div>
            </div>
            {providerModeError ? (
              <div className="mt-2">
                <InlineError>{providerModeError}</InlineError>
              </div>
            ) : null}
          </div>
        </AdminSectionCard>
      ) : null}

      {API_KEY_PROVIDERS.filter(
        (p) => p !== "claude" && p !== "bedrock",
      ).map((provider) => (
        <AdminSectionCard key={provider}>
          {renderProviderRow(provider)}
        </AdminSectionCard>
      ))}
    </div>
  );
}

export function ApiKeysScene() {
  return (
    <AdminSceneShell
      sceneName="ApiKeysScene"
      title="Provider keys"
      description="Rotate provider API keys and choose the Claude inference backend."
      headerActions={<HelpAnchor doc="workspace/admin" />}
    >
      <Commentable name="Panel">
        <ApiKeysPanelBody />
      </Commentable>
    </AdminSceneShell>
  );
}
